import { Injectable, inject, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { Firestore, collection, doc, getDoc, query, where, getDocs, onSnapshot } from '@angular/fire/firestore';
import { Functions } from '@angular/fire/functions';
import { httpsCallable } from './telemetry';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';

export interface Organization {
  id: string;
  name: string;
  /** 'pco' when connected to Planning Center, 'standalone' otherwise. */
  orgType?: 'pco' | 'standalone';
  createdAt: Date;
  createdBy: string;
  avatarUrl?: string;
  contactWebsite?: string;
  countryCode?: string;
  dateFormat?: string;
  timezone?: string;
  settings?: {
    [key: string]: any;
  };
  /** The SMS provider link. Written only by the sms* callables. */
  sms?: OrgSmsConfig;
}

export type SmsProviderName = 'winsms' | 'bulksms' | 'smsportal';

/** Mirrors OrgSmsConfig in functions/src/services/sms/types.ts. */
export interface OrgSmsConfig {
  provider: SmsProviderName;
  enabled: boolean;
  fallbackEnabledByDefault: boolean;
  failureCodes?: number[];
  optOutFooterEnabled: boolean;
  optOutFooterText: string;
  costPerCreditZar?: number | null;
  lastBalance?: number;
  lastBalanceAt?: any;
  webhookToken?: string;
  webhooksRegisteredAt?: any;
  webhooksError?: string | null;
  webhookLastSeenAt?: any;
  lastDlrAt?: any;
  webhookSigningEnabled?: boolean;
  configuredAt?: any;
  configuredBy?: string;
  disconnectedAt?: any;
}

export interface CreateOrganizationResult {
  organizationId: string;
  organizationName: string;
  orgType: 'pco' | 'standalone';
  requiredSteps: string[];
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {

  private firestore = inject(Firestore);
  private injector = inject(EnvironmentInjector);
  private functions = inject(Functions);

  private readonly createOrganizationFn = httpsCallable(this.functions, 'createOrganization');

  /**
   * Check if an organization exists by ID
   */
  getOrganization(orgId: string): Observable<Organization | null> {
    const orgRef = doc(this.firestore, 'organizations', orgId);
    return from(runInInjectionContext(this.injector, () => getDoc(orgRef))).pipe(
      map(docSnap => {
        if (docSnap.exists()) {
          return { id: docSnap.id, ...docSnap.data() } as Organization;
        }
        return null;
      })
    );
  }

  /**
   * Live view of an organization document. Emits on every change, so a settings
   * page sees what a callable just wrote without re-fetching.
   */
  watchOrganization(orgId: string): Observable<Organization | null> {
    return new Observable<Organization | null>((observer) => {
      const orgRef = doc(this.firestore, 'organizations', orgId);
      const unsubscribe = runInInjectionContext(this.injector, () => onSnapshot(orgRef, (snap) => {
        observer.next(snap.exists() ? ({ id: snap.id, ...snap.data() } as Organization) : null);
      }, (error) => observer.error(error)));
      return () => unsubscribe();
    });
  }

  /**
   * Get real-time updates for an organization document
   * This returns an Observable that emits whenever the organization document changes
   */
  getOrganizationRealtime(orgId: string): Observable<Organization | null> {
    return new Observable(observer => {
      const orgRef = doc(this.firestore, 'organizations', orgId);

      const unsubscribe = runInInjectionContext(this.injector, () => onSnapshot(orgRef,
        (docSnap) => {
          if (docSnap.exists()) {
            observer.next({ id: docSnap.id, ...docSnap.data() } as Organization);
          } else {
            observer.next(null);
          }
        },
        (error) => {
          observer.error(error);
        }
      ));

      return () => unsubscribe();
    });
  }

  /**
   * Create an organization.
   *
   * Server-side, via the createOrganization callable. It generates the document
   * id (the Planning Center org id, or a fresh one for a standalone org), sets
   * orgType and the onboarding step list, creates the encryption key, links the
   * caller as owner and updates their claims — none of which a client can be
   * trusted to do, which is why Firestore now denies org creation outright.
   *
   * @param orgName Display name for the organization.
   * @param details Optional profile details (Planning Center supplies these).
   */
  createOrganization(
    orgName: string,
    details?: {
      avatarUrl?: string;
      contactWebsite?: string;
      countryCode?: string;
      dateFormat?: string;
      timezone?: string;
    },
    /**
     * The creator's own name. Firebase Auth has no displayName for an email
     * sign-up, so without this the owner has no name anywhere — which is what
     * left campaigns with an empty sender.
     */
    userName?: string,
    /**
     * The creator's own mobile number, normalised to E.164 by the callable.
     *
     * A standalone organisation has no Planning Center profile to read numbers
     * from, so without this its owner cannot send a test message or validate a
     * template — and an unvalidated template cannot be used in a campaign.
     */
    userMobileNumber?: string
  ): Promise<CreateOrganizationResult> {
    return this.createOrganizationFn({ name: orgName, details, userName, userMobileNumber })
      .then(result => result.data as CreateOrganizationResult);
  }

  /**
   * Get user's organization info
   */
  getUserOrganization(userId: string): Observable<{ organizationId: string; organizationName: string } | null> {
    console.log('getUserOrganization called for userId:', userId);
    const userRef = doc(this.firestore, 'users', userId);
    return from(runInInjectionContext(this.injector, () => getDoc(userRef))).pipe(
      map(docSnap => {
        console.log('getUserOrganization - userId:', userId);
        console.log('getUserOrganization - docSnap.exists():', docSnap.exists());
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log('getUserOrganization - user document data:', data);

          // Check for organizationId
          const organizationId = data['organizationId'];
          const organizationName = data['organizationName'];

          if (organizationId) {
            console.log('✓ Found organization:', {
              organizationId: String(organizationId),
              organizationName: organizationName || 'Unknown Organization'
            });
            return {
              organizationId: String(organizationId),
              organizationName: organizationName || 'Unknown Organization'
            };
          } else {
            console.warn('User document exists but missing organization fields');
          }
        } else {
          console.warn('User document does not exist for userId:', userId);
        }
        return null;
      })
    );
  }

  /**
   * Get full user profile from Firestore
   */
  getUserProfile(userId: string): Observable<any> {
    const userRef = doc(this.firestore, 'users', userId);
    return from(runInInjectionContext(this.injector, () => getDoc(userRef))).pipe(
      map(docSnap => {
        if (docSnap.exists()) {
          return docSnap.data();
        }
        return null;
      })
    );
  }
}
